// src/components/Feed/MemberContent.js
// @flow

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import * as R from 'ramda';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { log, formatPhoneNumberForFrontend } from '../../utils/jsUtils';
import { setIsAppLoading } from '../../reduxStore/modules/common';
import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Hidden from '@mui/material/Hidden';
import Alert from '@mui/material/Alert';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import PaginatorFooter from '../SharedComponents/PaginateFooter';
import FeedTable from '../SharedComponents/FeedTable';
import AssignTaskDialog from './AssignTasksDialog';
import Mailto from '../Mailto';

import { useTheme } from '@mui/material/styles';
import memberContentStyles from './Styles/MemberContent.Style';
import commonCollapsibleListTableStyles from '../Styles/CommonCollapsibleListTable.Style';
import commonListTableStyles from '../Styles/CommonListTable.Style';
import ContentContainer from '../SharedComponents/ContentContainer';

import {
  setExportCSVRequests,
  setPrintRequests,
  setFilterSelections,
  selectRequests,
  selectFilterSelections,
  selectTotalRequests,
  selectFilterSearchText,
  selectExportCSVRequests,
  selectPrintRequests,
  selectUnits,
  fetchFeed
} from '../../reduxStore/modules/feed';

import { hideTagPicker } from '../../reduxStore/modules/tags/actions';
import { selectAccountSettings } from '../../reduxStore/modules/settings/selectors';
import { selectIsAppLoading, selectLoadingModules } from '../../reduxStore/modules/common/selectors';
import { selectTagPickerState, selectActiveCustomerId } from '../../reduxStore/modules/tags/selectors';
import { selectSubscriptionStatus } from '../../reduxStore/modules/billing/selectors';
import { logTagPicker } from '../../utils/debugUtils';
import { useI18n } from '../../contexts/I18nContext';
import { useAssignStaffToTasksMutation } from '../../features/api/staffApi';

// Debug constant
const DEBUG_ENABLED = process.env.NODE_ENV !== 'production';

type FeedData = {
  requests: Array<any>,
  units: Array<any>,
  totalRequests: number
};

type Props = {
  exportCSV: () => void,
  feedData?: FeedData,
  isFeedFetching: boolean
};

const MemberContent = ({ exportCSV, feedData, isFeedFetching }: Props) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const [showUnreadState, setShowUnreadState] = useState(false);
  const [openAssignTaskDialog, setOpenAssignTaskDialog] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const hasFetchedRef = useRef(false);
  // Track if initial load has completed to prevent content flashing during subsequent polls
  const hasCompletedInitialLoad = useRef(false);

  const user = useSelector(state => state.user.user);
  const units = useSelector(selectUnits);
  const requests = useSelector(selectRequests);
  const filterSelections = useSelector(selectFilterSelections);
  const totalRequests = useSelector(selectTotalRequests);
  const filterSearchText = useSelector(selectFilterSearchText);
  const exportCSVRequests = useSelector(selectExportCSVRequests);
  const printRequests = useSelector(selectPrintRequests);
  const accountSettings = useSelector(selectAccountSettings) || { Twilio: { Status: '' } };
  const isAppLoading = useSelector(selectIsAppLoading);
  const loadingModule = useSelector(selectLoadingModules);
  const defaultBuilding = useSelector(state => state.buildings.defaultBuilding);
  const i18n = useI18n();
  const subscriptionStatus = useSelector(selectSubscriptionStatus);
  const [assignStaffToTasks] = useAssignStaffToTasksMutation();

  // Tag picker state from Redux
  const isTagPickerOpen = useSelector(selectTagPickerState);
  const activeCustomerId = useSelector(selectActiveCustomerId);

  // Handle closing the tag picker
  const handleTagPickerClose = useCallback(() => {
    dispatch(hideTagPicker());
  }, [dispatch]);

  // Handle when a tag is picked from the picker
  const handleTagPicked = useCallback((tag) => {
    // The TagCustomersPicker component will handle the actual tag addition
    // This callback is just for any post-selection actions if needed
    log('[MemberContent] Tag picked:', tag);
  }, []);

  // Handle staff assignment to tasks (optimistic update via RTK Query)
  const handleAssignTasks = useCallback((tasks, assignedStaff) => {
    if (!tasks || tasks.length === 0 || !assignedStaff) return;

    // Get current requests for optimistic update
    const existingRequests = tasks.map(task =>
      requests.find(r => r._id === task._id)
    ).filter(Boolean);

    // Call RTK Query mutation (handles optimistic update internally)
    assignStaffToTasks({
      taskIds: tasks.map(t => t._id),
      assigneeId: assignedStaff._id,
      assignee: assignedStaff, // Full object for optimistic UI
      existingRequests
    });
  }, [assignStaffToTasks, requests]);

  // Note: Removed filteredRequests synchronization logic - now using requests directly
  
  // Listen for CSV export
  useEffect(() => {
    if (exportCSVRequests === true && requests.length > 0) {
      exportCSV();
      dispatch(setExportCSVRequests(false));
    }
  }, [exportCSVRequests, requests.length, exportCSV, dispatch]);

  // Listen for printing
  useEffect(() => {
    if (printRequests === true && requests.length > 0) {
      dispatch(setPrintRequests(false));
    }
  }, [printRequests, requests.length, dispatch]);

  // Make handleShowUnreadChange a stable callback
  const handleShowUnreadChange = useCallback((event) => {
    setShowUnreadState(event.target.checked);
    const newVal = event.target.checked ? 'true' : 'false';
    const newFilterSelections = {
      ...filterSelections,
      unreadMessagesOnlyOptions: [{ label: 'Unread Messages', value: newVal }]
    };
    dispatch(setFilterSelections(newFilterSelections));
    dispatch(setIsAppLoading(true));
  }, [dispatch, filterSelections]);

  // Determine if any filters are applied to show the persistent filter bar
  const hasFiltersApplied = useMemo(() => {
    return filterSelections.propertyOptions.length > 0 ||
           filterSelections.staffOptions.length > 0 ||
           filterSelections.tenantOptions.length > 0 ||
           filterSelections.tagOptions.length > 0 ||
           filterSelections.unreadMessagesOnlyOptions.length > 0;
  }, [
    filterSelections.propertyOptions,
    filterSelections.staffOptions,
    filterSelections.tenantOptions,
    filterSelections.tagOptions,
    filterSelections.unreadMessagesOnlyOptions
  ]);

  // Make markInProgress a stable callback
  const markInProgress = useCallback((requestId, newStatus, callbackSetLoading) => (e) => {
    e.stopPropagation();
    callbackSetLoading(prevState => !prevState);
    // updateRequest(requestId, newStatus, 'Request updated.', callbackSetLoading);
  }, []);

  // Make triggerOpenAssignTaskDialog a stable callback
  const triggerOpenAssignTaskDialog = useCallback((task) => (e) => {
    e.stopPropagation();
    setSelectedTasks([task]);
    setOpenAssignTaskDialog(true);
  }, []);

  // Memoize complex UI blocks to prevent unnecessary re-renders
  const accountStatusAlert = useMemo(() => {
    if (loadingModule.accountSettings) return null;

    if (accountSettings.Twilio && accountSettings.Twilio.Status === 'suspended') {
      return (
        <Box sx={memberContentStyles.errorCtr(theme)}>
          <Alert severity="error">
            {'Your account is suspended.'}
            <br />
            {'Please text us: +1 (917) 451-5515.'}
            <br />
            <span>
              {'Alternatively, email: '}
              <Mailto
                email="lio@askneo.io"
                headers={{ subject: 'Feedback on askneo.io console' }}
                obfuscate={false}
              >
                <span>{'lio@askneo.io'}</span>
              </Mailto>
            </span>
          </Alert>
        </Box>
      );
    }

    return null;
  }, [loadingModule.accountSettings, accountSettings.Twilio, theme]);

  const phoneNumberDisplay = useMemo(() => {
    if (defaultBuilding && defaultBuilding.Phone && formatPhoneNumberForFrontend(defaultBuilding.Phone)) {
      return (
        <>
          <Hidden smDown>
            <Typography sx={memberContentStyles.phoneNb(theme)} variant="h5">
              {`Your phone number: ${formatPhoneNumberForFrontend(defaultBuilding.Phone)}`}
            </Typography>
          </Hidden>
          <Hidden smUp>
            <Typography sx={memberContentStyles.phoneNbMobile(theme)} variant="h5">
              {`Your number: ${formatPhoneNumberForFrontend(defaultBuilding.Phone)}`}
            </Typography>
          </Hidden>
        </>
      );
    }
    return null;
  }, [defaultBuilding, theme]);

  const welcomeMessage = useMemo(() => {
    // Only show welcome message when:
    // 1. Not loading from any source (app, modules, or RTK Query) - BUT only for initial load
    // 2. No feed data exists
    // 3. No filters are applied
    // 4. Account has never had any requests (truly new account)

    // For INITIAL load: wait for all loading to complete
    // For SUBSEQUENT polls: don't re-hide content (hasCompletedInitialLoad.current = true)
    const isInitiallyLoading = !hasCompletedInitialLoad.current && (
      isAppLoading ||
      loadingModule.accountSettings ||
      loadingModule.feed ||
      isFeedFetching
    );

    // Mark initial load complete when all loading flags are false for the first time
    if (!isInitiallyLoading && !hasCompletedInitialLoad.current) {
      hasCompletedInitialLoad.current = true;
    }

    const hasNoFilters = filterSelections.propertyOptions.length === 0 &&
                        filterSelections.staffOptions.length === 0 &&
                        filterSelections.tenantOptions.length === 0 &&
                        filterSelections.tagOptions.length === 0 &&
                        filterSelections.unreadMessagesOnlyOptions.length === 0;

    const hasNoData = requests.length === 0;

    // Show welcome only after initial load, when there's no data and no filters
    if (!isInitiallyLoading && hasNoData && hasNoFilters && filterSearchText === '') {
      return (
        <Box sx={memberContentStyles.blockquoteCtr(theme)}>
          <blockquote className="tweet-this">
            <h3>Welcome to Neo!</h3>
            <p>Get your business phone number <a target='_blank' href='https://forms.gle/QJAhBLM5z1otacnr8'>certified</a> to start sending & receiving messages.</p>

            <p><b>To get started:</b><br />
            1. Take your mobile phone and text your new business phone number: {defaultBuilding ? formatPhoneNumberForFrontend(defaultBuilding.Phone) : ''}.<br />
            2. To send messages from this dashboard, you'll need to <a target='_blank' href='https://forms.gle/QJAhBLM5z1otacnr8'>certify your number here</a> <br />
            3. Once your number is certified, you can add more staff and enjoy 1:1 SMS messaging!</p>

            <p>Excited about automation? <a href='https://bit.ly/39c6mGo'>Check this video</a> :)</p>
            <p>Have questions? Text us at 917.451.5515 and we'll respond within 24h.</p>
            <p>Want to talk to someone? Book a Zoom meeting <a target='_blank' href='https://invite.askneo.io/cal'>here</a>.</p>
          </blockquote>
        </Box>
      );
    }
    return null;
  }, [
    isAppLoading,
    loadingModule.accountSettings,
    loadingModule.feed,
    isFeedFetching, // Added RTK Query loading state
    requests.length,
    filterSearchText, // Added to track search text
    filterSelections.propertyOptions,
    filterSelections.staffOptions,
    filterSelections.tenantOptions,
    filterSelections.tagOptions,
    filterSelections.unreadMessagesOnlyOptions,
    theme
  ]);

  return (
    <ContentContainer hasSubheader={true} sx={memberContentStyles.container(theme)}>
      {/* Certification Banner - Only show during trial */}
      {
        subscriptionStatus === 'trialing' && requests && requests.length > 0
        ? <Box
            sx={
              requests && requests.length > 0
                ? memberContentStyles.certificationBannerFeed(theme)
                : memberContentStyles.certificationBannerWelcome(theme)
            }
          >
            <Alert severity="warning">
              {'You\'re currently limited to receiving messages only. To enable sending, please '}
              <Link
                to="/activate"
                style={{ color: '#1976d2', textDecoration: 'underline', fontWeight: 600 }}
              >
                {'certify your number here'}
              </Link>
              {'.'}
            </Alert>
          </Box>
        : null
      }

      {accountStatusAlert}
      {phoneNumberDisplay}

      {/* Persistent Filter Bar - Always visible when filters are applied or when there are requests */}
      {(hasFiltersApplied || totalRequests > 1 || (requests && requests.length > 0)) && (
        <Box sx={memberContentStyles.persistentFilterBar(theme)}>
          {/* Customer count on the left */}
          {requests && requests.length > 0 && units && units.length > 0 && (
            <Typography variant="body1">
              {
                filterSearchText !== ''
                ? `${requests.length} results in page ${filterSelections.pageNb + 1}`
                : `${requests.length} of ${totalRequests} Customers`
              }
            </Typography>
          )}
          {/* Toggle on the right */}
          <FormControlLabel
            sx={memberContentStyles.filterButton(theme)}
            control={
              <Switch
                checked={showUnreadState}
                color="primary"
                name="checked"
                onChange={handleShowUnreadChange}
              />
            }
            label="Show unread"
          />
        </Box>
      )}

      {welcomeMessage}

      {/* No results message for search with no results (search/filters yield nothing) */}
      {
        !isAppLoading && !loadingModule.feed && !isFeedFetching &&
        requests.length === 0 &&
        (filterSearchText.trim() !== '' ||
         filterSelections.propertyOptions.length > 0 ||
         filterSelections.staffOptions.length > 0 ||
         filterSelections.tenantOptions.length > 0 ||
         filterSelections.tagOptions.length > 0 ||
         filterSelections.unreadMessagesOnlyOptions.length > 0)
          ? (
            <Box sx={memberContentStyles.noResultsMessageCtr(theme)}>
              <SearchRoundedIcon sx={memberContentStyles.searchIcon(theme)} />
              <Typography variant="h5">
                {filterSearchText.trim() !== '' ? 'No results.' : 'No results found.'}
              </Typography>
            </Box>
          )
          : null
      }


      {
        requests && requests.length > 0 && units && units.length > 0
        ? <FeedTable
            markInProgress={markInProgress}
            triggerAssignDialog={triggerOpenAssignTaskDialog}
          />
        : null
      }


      {requests && requests.length > 0 || filterSearchText !== ''
        ? <PaginatorFooter />
        : null
      }

      <AssignTaskDialog
        open={openAssignTaskDialog}
        setOpen={setOpenAssignTaskDialog}
        tasks={selectedTasks}
        triggerAction={handleAssignTasks}
      />

    </ContentContainer>
  );
};

MemberContent.propTypes = {
  exportCSV: PropTypes.func.isRequired,
  feedData: PropTypes.shape({
    requests: PropTypes.array,
    units: PropTypes.array,
    totalRequests: PropTypes.number
  }),
  isFeedFetching: PropTypes.bool
};

export default React.memo(MemberContent);